import React, { useEffect, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { bootStateMachine } from '@/lib/boot/BootStateMachine';

export const AppShellSkeleton = () => {
  const [shouldHide, setShouldHide] = useState(false);
  
  // 🔥 NEW: Self-healing escape hatch with boot state check
  useEffect(() => {
    // If skeleton visible for >3 seconds, check if stuck
    const maxSkeletonTime = setTimeout(() => {
      if (document.visibilityState === 'visible') {
        // Check if boot state is terminal (prevents blank screen)
        const currentState = bootStateMachine?.getCurrentState()?.phase;
        if (currentState === 'LICENSED' || currentState === 'ERROR') {
          console.error('[SKELETON] Stuck in terminal state - forcing hide');
          setShouldHide(true);
        } else {
          console.log('[SKELETON] Still loading legitimately (state:', currentState, ')');
        }
      }
    }, 3000);
    
    // Force hide when tab becomes visible (with delay)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        console.log('[SKELETON] Tab became visible, will hide in 100ms');
        setTimeout(() => {
          const currentState = bootStateMachine?.getCurrentState()?.phase;
          if (currentState === 'LICENSED' || currentState === 'ERROR') {
            setShouldHide(true);
          }
        }, 100);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibility);
    
    return () => {
      clearTimeout(maxSkeletonTime);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);
  
  // 🔥 NEW: Hide skeleton if escape hatch triggered
  if (shouldHide) {
    console.log('[SKELETON] Escape hatch triggered - hiding skeleton');
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header Skeleton */}
      <header className="border-b bg-card">
        <div className="flex h-16 items-center gap-4 px-6">
          <Skeleton className="h-8 w-8 rounded" />
          <Skeleton className="h-6 w-32" />
          <div className="ml-auto flex items-center gap-4">
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-8 w-8 rounded-full" />
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar Skeleton */}
        <aside className="w-64 border-r bg-card">
          <div className="p-4 space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 flex-1" />
              </div>
            ))}
          </div>
        </aside>

        {/* Main Content Skeleton */}
        <main className="flex-1 p-6">
          <div className="space-y-6">
            {/* Dashboard Cards Skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Card key={i}>
                  <CardHeader className="pb-3">
                    <Skeleton className="h-4 w-24" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-8 w-16 mb-2" />
                    <Skeleton className="h-3 w-20" />
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Chart Skeleton */}
            <Card>
              <CardHeader>
                <Skeleton className="h-5 w-40" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-64 w-full rounded" />
              </CardContent>
            </Card>

            {/* Additional Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {Array.from({ length: 2 }).map((_, i) => (
                <Card key={i}>
                  <CardHeader>
                    <Skeleton className="h-5 w-32" />
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};